import React, { useEffect, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import { toast } from "react-toastify";
import { getAssessmentInfo, putAssessmentInfo, resetGetAssessmentInfo } from "../../redux/actions/assessmentInfo/assessmentInfo";
import { ProfileOverviewState } from "../../redux/reducers/getProfile/getProfileOverviewReducer";
import { SaveDataState } from "../../redux/reducers/getSaveData/saveDataReducer";
import { SaveResultState } from "../../redux/reducers/getSaveData/saveResultReducer";
import { AggregateSiteAssessmentInfoState } from "../../types/assessment_info/AggregateSiteAssessmentInfoState";
import { selectELSLookups, selectGetAggregateSiteAssessmentInfo, selectProfileGetOverview, selectSaveData, selectSaveResult } from "../../redux/selectors/exportSelectorPaths";
import { useTranslation } from "react-i18next";
import { AggregateSiteAssessmentInfo, AggregateSiteLandAttributeSchema, defaultAggregateSiteAssessmentInfo, AggregateOptionWithFlag, AggregateLandType, AggregateOption, defaultAggregateSiteAnalysisTypes } from "../../types/assessment_info/aggregateSites/AggregateSiteData";
import { GetElsLookupsState } from "../../redux/reducers/getLookups/getElsLookupsReducer";
import { getElsLookups } from "../../redux/actions/lookups/lookups";
import { ControlledAccordion, useAccordionProvider } from '@szhsin/react-accordion'
import AccordionItem, { DefaultAccordionProviderOptions } from '../../components/common/AccordionItem/AccordionItem'
import "./Summary.css"
import { isValidationResult, useFormErrorContext } from "../../utils/context/FormErrorContext";
import i18n from "../../i18n";
import ErrorMessages from "../../components/common/ErrorMessages/ErrorMessages";
import WarningMessages from "../../components/common/WarningMessages/WarningMessages";
import { useFormWarningContext } from "../../utils/context/FormWarningContext";

export default function AGSSummary(): JSX.Element {
    const dispatch = useDispatch();
    const profileOverview: ProfileOverviewState = useSelector(selectProfileGetOverview);
    const assessmentInfoState: AggregateSiteAssessmentInfoState = useSelector(selectGetAggregateSiteAssessmentInfo)
    const currentSnapshotId = sessionStorage.getItem("currentSnapshotId") as string;
    const [isUpdateReqSent, setIsUpdateReqSent] = useState(false);
    const saveData: SaveDataState = useSelector(selectSaveData)
    const saveResultState: SaveResultState = useSelector(selectSaveResult);
    const elsLookupsState: GetElsLookupsState = useSelector(selectELSLookups);
    const [assessmentInfo, setAssessmentInfo] = useState(defaultAggregateSiteAssessmentInfo);
    const { t } = useTranslation("ags_summary");
    const providerValue = useAccordionProvider(DefaultAccordionProviderOptions)
    const { errors, clearErrors } = useFormErrorContext();
    const { warnings, warningDisplayed, setWarningDisplayed, clearWarnings } = useFormWarningContext();
    const [pondsCount, setPondsCount] = useState({ "1": { count: 0, area: 0 }, "2": { count: 0, area: 0 } })
    const [waterExtractionsCount, setWaterExtractionsCount] = useState({ "N": { count: 0, area: 0 }, "E": { count: 0, area: 0 }, "S": { count: 0, area: 0 }, "W": { count: 0, area: 0 }, "NE": { count: 0, area: 0 }, "SE": { count: 0, area: 0 }, "SW": { count: 0, area: 0 }, "NW": { count: 0, area: 0 } })
    const [pondsTotal, setPondsTotal] = useState(0)
    const [waterExtractionsTotal, setWaterExtractionsTotal] = useState(0)
    const [licensedLandUomText, setLicensedLandUomText] = useState<string | null>(null)
    const [unlicensedLandUomText, setUnlicensedLandUomText] = useState<string | null>(null)
    const [pondUomText, setPondUomText] = useState<string | null>(null)
    const profileType = profileOverview.profile.profile_type_code;
    const [dataError, setDataError] = useState(false)
    const isInternal = (sessionStorage.getItem("isInternalUser") as string) === "true"

    const formatAreaValue = (value: number | null | undefined) => {
        const safeValue = value ?? 0
        return safeValue.toLocaleString(i18n.language, {
            minimumFractionDigits: 2,
            maximumFractionDigits: 2
        })
    }

    useEffect(() => {
        clearErrors();
        clearWarnings();
        // eslint-disable-next-line
      }, [])

    useEffect(() => {
        if (!elsLookupsState.dropdowns) {
            const propertyCode = sessionStorage.getItem("propertyCode") ?? "";
            dispatch(getElsLookups(propertyCode))
        }
    }, [elsLookupsState.dropdowns, dispatch]);

    useEffect(() => {
        let data = { ...assessmentInfo }
        if (Object.keys(assessmentInfoState.formData).length !== 0) {
            data = { ...assessmentInfoState.formData as AggregateSiteAssessmentInfo }
        }
        if (!data["analysisTypes"] || data["analysisTypes"]?.length === 0) {
            data["analysisTypes"] = defaultAggregateSiteAnalysisTypes
        }
        data["analysisTypes"].forEach(e => e.componentId = String(crypto.randomUUID()))
        setAssessmentInfo(data)
        const llUom = (elsLookupsState.dropdowns.areaUom as AggregateOptionWithFlag[])?.filter(e => e.code === data.land?.licensedLandUom)[0]
        const ulUom = (elsLookupsState.dropdowns.areaUom as AggregateOptionWithFlag[])?.filter(e => e.code === data.land?.unlicensedLandUom)[0]
        const pondUom = (elsLookupsState.dropdowns.areaUom as AggregateOptionWithFlag[])?.filter(e => e.code === data.pond?.areaUom)[0]
        setLicensedLandUomText(llUom ? (i18n.language === "en" ? llUom?.description : llUom?.frenchDescription) : "")
        setUnlicensedLandUomText(ulUom ? (i18n.language === "en" ? ulUom?.description : ulUom?.frenchDescription) : "")
        setPondUomText(pondUom ? (i18n.language === "en" ? pondUom?.description : pondUom?.frenchDescription) : "")
        // eslint-disable-next-line
    }, [assessmentInfoState, elsLookupsState.dropdowns, i18n.language])

    useEffect(() => {
        if (currentSnapshotId && profileOverview.profile.profile_type_code && !assessmentInfoState.loading
            && (!assessmentInfoState.success || currentSnapshotId !== assessmentInfoState.snapshot_id)) {
            if (!dataError) {
                dispatch(getAssessmentInfo(currentSnapshotId, profileOverview.profile.profile_type_code))
                if (!assessmentInfoState.success && assessmentInfoState.error) {
                    toast.error(assessmentInfoState.message || "Failed to find assessment info with snapshot id");
                    setDataError(true)
                }
            }
        }
    }, [assessmentInfoState, currentSnapshotId, dispatch, profileOverview.profile.profile_type_code, dataError]);

    useEffect(() => {
        // Trigger save only if context changed
        if (saveData.saving) {
            clearErrors()
            clearWarnings()
            let spectralSection = null
            if (profileType === "AGS") {
                spectralSection = "ags-summary"
            }
            const data = {
                licenseOperationTypeCode: assessmentInfo["licenseOperationTypeCode"],
                licenseActiveFlag: assessmentInfo["licenseActiveFlag"],
                licenses: assessmentInfo["licenses"],
                surrenderRelinquishLicenseFlag: assessmentInfo["surrenderRelinquishLicenseFlag"],
                surrenderRelinquishLicenseDate: assessmentInfo["surrenderRelinquishLicenseDate"],
                rehabilitatedLicensedLandFlag: assessmentInfo["rehabilitatedLicensedLandFlag"],
                unlicensedLandFlag: assessmentInfo["unlicensedLandFlag"],
                licensedPondFlag: assessmentInfo["licensedPondFlag"],
                belowWaterExtractionFlag: assessmentInfo["belowWaterExtractionFlag"],
                attachments: assessmentInfo["attachments"],
                land: assessmentInfo["land"],
                pond: assessmentInfo["pond"],
                analysisTypes: assessmentInfo["analysisTypes"],
                licensedLands1: assessmentInfo["licensedLands1"],
                licensedLands2: assessmentInfo["licensedLands2"],
                licensedLands3: assessmentInfo["licensedLands3"],
                unlicensedLands: assessmentInfo["unlicensedLands"]
            }
            dispatch(putAssessmentInfo(currentSnapshotId, {
                "data": { ...data },
                "spectral_section": spectralSection,
                "trigger_spectral": true,
                "profile_type_code": "AGS",
                "is_internal": isInternal,
                "allow_warning_override": warningDisplayed
            }));
            setIsUpdateReqSent(true);
        }
    }, [assessmentInfoState.formData, currentSnapshotId, dispatch, saveData.saving, assessmentInfo, clearErrors, clearWarnings, profileType, isInternal, warningDisplayed]);

    useEffect(() => {
        if (isUpdateReqSent && !saveResultState.loading) {
            if (saveResultState.status === 200) {
                dispatch(resetGetAssessmentInfo())
                dispatch(getAssessmentInfo(currentSnapshotId, profileOverview.profile.profile_type_code));
            } else {
                if (saveResultState.result && isValidationResult(saveResultState.result)){
                    if (saveResultState.result.errors.length > 0) {
                        toast.error("Failed to save data. Please fix errors and try again");
                    } else {
                        if (saveResultState.result.warnings.length > 0) {
                            setWarningDisplayed(true)
                        }
                        dispatch(getAssessmentInfo(currentSnapshotId, profileOverview.profile.profile_type_code));
                    }
                }
            }
            setIsUpdateReqSent(false);
        }
    }, [currentSnapshotId, dispatch, isUpdateReqSent, profileOverview.profile.profile_type_code, saveResultState, warnings, errors, setWarningDisplayed]);

    const getDescription = (type: string, code: string) => {
        if (elsLookupsState.dropdowns?.aggregateLandType) {
            if (i18n.language === 'fr') {
                if (type === "unlicensed" && (code === "1" || code === "2")) {
                    return (elsLookupsState.dropdowns?.aggregateLandType as AggregateOptionWithFlag[])?.find(e => e.code === code)?.frenchDescription?.trim().split(" ")[0] || ""
                }
                return (elsLookupsState.dropdowns?.aggregateLandType as AggregateOptionWithFlag[])?.find(e => e.code === code)?.frenchDescription?.trim() || ""
            }
            if (type === "unlicensed" && (code === "1" || code === "2")) {
                return (elsLookupsState.dropdowns?.aggregateLandType as AggregateOptionWithFlag[])?.find(e => e.code === code)?.description?.trim().split(" ")[0] || ""
            }
            return (elsLookupsState.dropdowns?.aggregateLandType as AggregateOptionWithFlag[])?.find(e => e.code === code)?.description?.trim() || ""
        }
        return ""
    }

    const getTotalArea = (lands: AggregateLandType[], uom: string) => {
        let sum = 0
        lands?.forEach((e: AggregateLandType) => sum += e.area ?? 0)
        return formatAreaValue(sum) + " " + uom
    }

    useEffect(() => {
        const tempPondsCount = { "1": { count: 0, area: 0 }, "2": { count: 0, area: 0 } }
        const tempWaterExtractionsCount = { "N": { count: 0, area: 0 }, "E": { count: 0, area: 0 }, "S": { count: 0, area: 0 }, "W": { count: 0, area: 0 }, "NE": { count: 0, area: 0 }, "SE": { count: 0, area: 0 }, "SW": { count: 0, area: 0 }, "NW": { count: 0, area: 0 } }
        let tempPondsTotal = 0
        let tempWaterExtractionsTotal = 0
        assessmentInfo.pond?.pondTypes?.forEach(e => {
            if (e.pondTypeCode && tempPondsCount[e.pondTypeCode as keyof typeof tempPondsCount] !== null) {
                tempPondsCount[e.pondTypeCode as keyof typeof tempPondsCount]["count"] = tempPondsCount[e.pondTypeCode as keyof typeof tempPondsCount]["count"] + 1
                tempPondsCount[e.pondTypeCode as keyof typeof tempPondsCount]["area"] = tempPondsCount[e.pondTypeCode as keyof typeof tempPondsCount]["area"] + (e.area || 0)
                tempPondsTotal += (e.area || 0)
            }
        })
        assessmentInfo.pond?.waterExtractions?.forEach(e => {
            if (e.locationCode && tempWaterExtractionsCount[e.locationCode as keyof typeof tempWaterExtractionsCount] !== null) {
                tempWaterExtractionsCount[e.locationCode as keyof typeof tempWaterExtractionsCount]["count"] = tempWaterExtractionsCount[e.locationCode as keyof typeof tempWaterExtractionsCount]["count"] + 1
                tempWaterExtractionsCount[e.locationCode as keyof typeof tempWaterExtractionsCount]["area"] = tempWaterExtractionsCount[e.locationCode as keyof typeof tempWaterExtractionsCount]["area"] + (e.area || 0)
                tempWaterExtractionsTotal += (e.area || 0)
            }
        })
        setPondsCount(tempPondsCount)
        setWaterExtractionsCount(tempWaterExtractionsCount)
        setPondsTotal(tempPondsTotal)
        setWaterExtractionsTotal(tempWaterExtractionsTotal)
    }, [assessmentInfo])

    return (
        <>
            <ErrorMessages errors={errors} />
            <WarningMessages warnings={warnings} />
            <div className="summary-container summary-page-container">
                <div className="summary-heading margin-bottom-1">{t("heading1")}</div>
                <div className="partial-width">
                    <ControlledAccordion providerValue={providerValue}>
                        <AccordionItem header={t("licensedLands", { ns: 'ags_summary' })}
                            key="licensedLands"
                            itemKey="licensedLands"
                            headingLevel={2}
                            textRight={formatAreaValue(assessmentInfo.land?.totalLicensedLandArea) + " " + licensedLandUomText}>
                            <div className="summary-grid">
                                <div className='summary-section-container'>
                                    <table className='summary-table income-expense-summary-table' role='presentation'>
                                        <thead className='invisible-header'><tr><th></th></tr></thead>
                                        <tbody>
                                            <tr><td>{t("totalIntegral")}</td><td>{formatAreaValue(assessmentInfo.land?.totalIntegralLandArea) + " " + licensedLandUomText}</td></tr>
                                            {assessmentInfo.licensedLands1?.map((e: AggregateSiteLandAttributeSchema, i: number) =>
                                                <>
                                                    {e.landTypeCode !== "totalLicensed" && e.landTypeCode !== "totalIntegral" ? <tr key={i}>
                                                        <td>{t(getDescription("licensed", e.landTypeCode as string) as string)}</td>
                                                        <td>{formatAreaValue(e?.area) + " " + licensedLandUomText}</td>
                                                    </tr> : <></>}
                                                </>
                                            )}
                                        </tbody>
                                    </table>
                                </div>
                                <div className='summary-section-container'>
                                    <table className='summary-table income-expense-summary-table' role='presentation'>
                                        <thead className='invisible-header'><tr><th></th></tr></thead>
                                        <tbody>
                                            <tr><td>{t("totalAdditional")}</td><td>{getTotalArea(assessmentInfo.licensedLands2 as AggregateLandType[], licensedLandUomText as string)}</td></tr>
                                            {assessmentInfo.licensedLands2?.map((e: AggregateSiteLandAttributeSchema, i: number) =>
                                                <tr key={i}>
                                                    <td>{t(getDescription("licensed", e.landTypeCode as string) as string)}</td>
                                                    <td>{formatAreaValue(e?.area) + " " + licensedLandUomText}</td>
                                                </tr>
                                            )}
                                        </tbody>
                                    </table>
                                </div>
                                <div className='summary-section-container'>
                                    <table className='summary-table income-expense-summary-table' role='presentation'>
                                        <thead className='invisible-header'><tr><th></th></tr></thead>
                                        <tbody>
                                            <tr><td>{t("totalRehabilitated")}</td><td>{getTotalArea(assessmentInfo.licensedLands3 as AggregateLandType[], licensedLandUomText as string)}</td></tr>
                                            {assessmentInfo.licensedLands3?.map((e: AggregateSiteLandAttributeSchema, i: number) =>
                                                <tr key={i}>
                                                    <td>{t(getDescription("licensed", e.landTypeCode as string) as string)}</td>
                                                    <td>{formatAreaValue(e?.area) + " " + licensedLandUomText}</td>
                                                </tr>
                                            )}
                                        </tbody>
                                    </table>
                                </div>
                            </div>
                        </AccordionItem>
                    </ControlledAccordion>
                    <ControlledAccordion providerValue={providerValue}>
                        <AccordionItem header={t("licensedPonds", { ns: 'ags_summary' })}
                            key="licensedPonds"
                            itemKey="licensedPonds"
                            headingLevel={2}>
                            <div className="summary-grid">
                                <div className='summary-section-container'>
                                    <table className='summary-table income-expense-summary-table' role='presentation'>
                                        <thead className='invisible-header'><tr><th></th></tr></thead>
                                        <tbody>
                                            <tr>
                                                <td>{t("totalPonds")}</td>
                                                <td className="ags-licensed-ponds-count">{assessmentInfo.pond?.pondTypes?.length || 0}</td>
                                                <td>{formatAreaValue(pondsTotal) + " " + pondUomText}</td>
                                            </tr>
                                            {(elsLookupsState.dropdowns["aggregatePondType"] as AggregateOption[]).map((e: AggregateOption, i: number) =>
                                                <>
                                                    {pondsCount[e.code as keyof typeof pondsCount] && pondsCount[e.code as keyof typeof pondsCount]["count"] ?
                                                        <tr key={i}>
                                                            <td>{i18n.language === "en" ? e.description : e.frenchDescription}</td>
                                                            <td className="ags-licensed-ponds-count">{pondsCount[e.code as keyof typeof pondsCount]["count"]}</td>
                                                            <td>{formatAreaValue(pondsCount[e.code as keyof typeof pondsCount]["area"]) + " " + pondUomText}</td>
                                                        </tr> : <></>}
                                                </>
                                            )}
                                        </tbody>
                                    </table>
                                </div>
                                <div className='summary-section-container'>
                                    <table className='summary-table income-expense-summary-table' role='presentation'>
                                        <thead className='invisible-header'><tr><th></th></tr></thead>
                                        <tbody>
                                            <tr>
                                                <td>{t("totalBelowWaterExtractions")}</td>
                                                <td className="ags-licensed-ponds-count">{assessmentInfo.pond?.waterExtractions?.length || 0}</td>
                                                <td>{formatAreaValue(waterExtractionsTotal) + " " + pondUomText}</td>
                                            </tr>
                                            {(elsLookupsState.dropdowns["aggregateWaterExtractionLocation"] as AggregateOption[]).map((e: AggregateOption, i: number) =>
                                                <>
                                                    {waterExtractionsCount[e.code as keyof typeof waterExtractionsCount] && waterExtractionsCount[e.code as keyof typeof waterExtractionsCount]["count"] ?
                                                        <tr key={i}>
                                                            <td>{i18n.language === "en" ? e.description : e.frenchDescription}</td>
                                                            <td className="ags-licensed-ponds-count">{waterExtractionsCount[e.code as keyof typeof waterExtractionsCount]["count"]}</td>
                                                            <td>{formatAreaValue(waterExtractionsCount[e.code as keyof typeof waterExtractionsCount]["area"]) + " " + pondUomText}</td>
                                                        </tr> : <></>}
                                                </>
                                            )}
                                        </tbody>
                                    </table>
                                </div>
                            </div>
                        </AccordionItem>
                    </ControlledAccordion>
                    <ControlledAccordion providerValue={providerValue}>
                        <AccordionItem header={t("unlicensedLands", { ns: 'ags_summary' })}
                            key="unlicensedLands"
                            itemKey="unlicensedLands"
                            headingLevel={2}
                            textRight={getTotalArea(assessmentInfo.unlicensedLands as AggregateLandType[], unlicensedLandUomText as string)}>
                            <div className='summary-section-container-unlicensed'>
                                <div className="summary-grid">
                                    <div className='summary-section-container-unlicensed'>
                                        <table className='summary-table income-expense-summary-table' role='presentation'>
                                            <thead className='invisible-header'><tr><th></th></tr></thead>
                                            <tbody>
                                                <tr><td>{t("totalUnlicensedArea")}</td><td>{getTotalArea(assessmentInfo.unlicensedLands as AggregateLandType[], unlicensedLandUomText as string)}</td></tr>
                                                {assessmentInfo.unlicensedLands?.map((e: AggregateSiteLandAttributeSchema, i: number) =>
                                                    <tr key={i}>
                                                        <td>{t(getDescription("unlicensed", e.landTypeCode as string) as string)}</td>
                                                        <td>{formatAreaValue(e?.area) + " " + unlicensedLandUomText}</td>
                                                    </tr>
                                                )}
                                            </tbody>
                                        </table>
                                    </div>
                                </div>
                            </div>
                        </AccordionItem>
                    </ControlledAccordion>
                    <ControlledAccordion providerValue={providerValue}>
                        <AccordionItem header={t("documents", { ns: 'ags_summary' })}
                            key="unlicensedLands"
                            itemKey="unlicensedLands"
                            headingLevel={2}>
                            <div className="summary-grid">
                                <div className='summary-section-container'>
                                    <table className='summary-table income-expense-summary-table' role='presentation'>
                                        <thead className='invisible-header'><tr><th></th></tr></thead>
                                        <tbody>
                                            <tr><td>{t("totalUploaded")}</td><td>{assessmentInfo.attachments?.length}</td></tr>
                                        </tbody>
                                    </table>
                                </div>
                            </div>
                        </AccordionItem>
                    </ControlledAccordion>
                </div>
            </div>
        </>
    )
}
